// ----------------------------------------------------------------------
// <copyright file="Izenda.ConnectionDetailStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var { isEmpty, isNil, get } = require('lodash/fp');

  var m = require('mobx');
  var dataFetcher = require('fetchers/Izenda.ConnectionStringDataFetcher');
  var uiState = require('logic/Izenda.UiState.js');
  var constants = require('logic/data/Izenda.ConnectionConstants');
  var localizer = require('share/localization/Izenda.Localizer');

  /**
   * Call testConnectionString API
   * @param  {ConnectionDetail} connectionDetail
   * @return {Promise} A promise of API invoke
   */
  function testConnectionApi(connectionDetail) {
    var connectionStringRequest = {
      ServerTypeId: connectionDetail.serverTypeId,
      ConnectionString: connectionDetail.connectionString,
      ConnectionId: connectionDetail.id
    };
    return dataFetcher.testConnectionString(connectionStringRequest);
  }

  /**
   * Call validateSchemaApi API
   * @param  {ConnectionDetail} connectionDetail
   * @return {Promise} A promise of API invoke
   */
  function validateSchemaApi(connectionDetail) {
    var connectionStringRequest = connectionDetail.getData(true);
    return dataFetcher.validateSchema(connectionStringRequest);
  }

  /**
   * Call getDatabaseName API
   * @param  {ConnectionDetail} connectionDetail
   * @return {Promise} A promise of API call
   */
  function getDbNameApi(connectionDetail) {
    var getDatabaseNameRequest = {
      ServerTypeId: connectionDetail.serverTypeId,
      ConnectionString: connectionDetail.connectionString
    };

    return dataFetcher.getDatabaseName(getDatabaseNameRequest);
  }

  /**
   * It encapsulates the connection detail app logic (side-effect likes API invoke
   * and UI logic)
   * @class ConnectionDetailStore
   * @param {ConnectionStore} masterStore Connection store
   */
  function ConnectionDetailStore(masterStore) {
    this.masterStore = masterStore;
    this.onInvalidConnectionString = function(msg) {};

    var props = {
      item: function() {
        return this.masterStore.item;
      },
      dbTypeList: function() {
        return this.masterStore.dbTypeList;
      },
      tenantId: function() {
        return uiState.getTenantId();
      }
    };

    m.extendObservable(this, props);
  }
  /**
   * link between store and uiState\
   */
  ConnectionDetailStore.prototype.init = m.action(function() {
    uiState.setCheckDirtyFunction(
      function() {
        return this.item.needConfirmWhenLeaving;
      }.bind(this)
    );
  });

  /**
   * unlink between store and uiState\
   */
  ConnectionDetailStore.prototype.destroy = m.action(function() {
    uiState.resetCheckDirtyFunction();
  });
  /**
   * Load connection data by its id from API
   * @return {Promise} A promise of loading connection data
   */
  ConnectionDetailStore.prototype.load = m.action(function() {
    var store = this;

    var connectionDetail = this.item;
    var setDetailToStore = m.action(function(detail) {
      connectionDetail.setConnectionStringStatus();
      connectionDetail.readDetail(detail);
      return detail;
    });

    if (!isEmpty(connectionDetail.id)) {
      // Only load connection if its id existed.
      return dataFetcher
        .getConnectionString(connectionDetail.id)
        .then(get('connection'))
        .then(setDetailToStore)
        .then(store.connectToDataSource.bind(store));
    } else {
      // If it is new (its id is emtpy), just read its default detail.
      return Promise.resolve();
      // return store.masterStore.addConnection();
    }
  });

  ConnectionDetailStore.prototype.reloadDetectChanges = m.action(function() {
    var connectionDetail = this.item;
    if (isEmpty(connectionDetail.id)) {
      return;
    }

    var updateChanges = function(detail) {
      connectionDetail.setChange(detail.physicalChange);
      uiState.increaseDetectChangeCount();
      return detail;
    };

    // Only load connection if its id existed.
    return dataFetcher
      .getConnectionString(connectionDetail.id)
      .then(get('connection'))
      .then(updateChanges);
  });

  /**
   * Connect to data source by input connection string in order to getting
   * its database schema including its physical shema changes against previous
   * saved schema.
   * @param  {ConnectionDetail} detail
   * @param  {bool} force  Force re-load data source regardless to its existing one.
   */
  ConnectionDetailStore.prototype.connectToDataSource = m.action(function(detail, force) {
    var isEmptyDataSource = isEmpty(detail.dbSource);
    var needReloadDetectChange = detail.needReloadDetectChange;
    // Retrieve data source from API as current data source is empty or force to do so
    var retrieveDataSource = null;

    if (!isEmptyDataSource && !force) {
      // don't need to call api
      retrieveDataSource = Promise.resolve({
        dBSource: detail.dbSource
      });
    } else {
      // need to call api
      retrieveDataSource = !needReloadDetectChange
        ? dataFetcher.getRemoteSchema(detail.connectionString, detail.serverTypeId)
        : dataFetcher.reloadRemoteSchema(detail);
    }

    var updateDbSource = m.action(
      function(result) {
        if (result.success === false) {
          this.updateErrorMessage(result);
        } else {
          this.item.readDbSource(result.dBSource);
          if ((!isEmptyDataSource || force) && needReloadDetectChange) {
            // reload change to detail & notify changes to contextmenu
            this.reloadDetectChanges();
          }
        }
        return null;
      }.bind(this)
    );

    return retrieveDataSource.then(updateDbSource);
  });

  /**
   * Populate database name by current connection string when user doesn't
   * enter its name.
   * @return {Promise}
   */
  ConnectionDetailStore.prototype.populateDatabaseName = m.action(function() {
    var connectionDetail = this.item;
    return isEmpty(connectionDetail.name)
      ? getDbNameApi(connectionDetail).then(function(dbName) {
          dbName && connectionDetail.setName(dbName);
          return !isEmpty(dbName);
        })
      : Promise.resolve(false);
  });

  /**
   * Validate current connection string
   * @return {Promise}
   */
  ConnectionDetailStore.prototype.validateConnection = function() {
    var connectionDetail = this.item;
    return !connectionDetail.connectionString
      ? Promise.resolve('')
      : testConnectionApi(connectionDetail)
          .then(
            function(result) {
              connectionDetail.readTestResult(result);
              return result.messages;
            }.bind(this)
          )
          .then(function(errorMessages) {
            var isSuccess = isNil(errorMessages) || isEmpty(errorMessages);
            connectionDetail.setConnectionStringValid(isSuccess);
            return errorMessages;
          });
  };

  ConnectionDetailStore.prototype.validateSchema = function() {
    var connectionDetail = this.item;
    if (connectionDetail.notNeedToValidateSchema) {
      return Promise.resolve(true);
    } else if (connectionDetail.isNew) {
      return Promise.resolve(false);
    }

    return validateSchemaApi(connectionDetail).then(function(success) {
      connectionDetail.readStatusValidateSchema(success);
      return success;
    });
  };

  ConnectionDetailStore.prototype.save = m.action('save', function() {
    var data = this.item.getData();
    data.tenantId = uiState.getTenantId();

    var reloadStoreAfterSaved = m.action(
      function(result) {
        if (result.success && isEmpty(data.id)) {
          // Get server type short name from the list and assign back to
          // current connection detail in case of adding new connection.
          var serverType = this.dbTypeList.find(function(item) {
            return item.id === data.serverTypeId;
          });
          this.item.serverTypeName = serverType.shortName;
          this.item.id = result.connectionId;
          this.masterStore.addConnectionToList(this.item);
          uiState.increaseDetectChangeVisibleConnection();
        } else {
          /**
           * If this connection has changes:
           *   - Reload datasourcetree
           *   - Reload change to detail
           *   - Notify changes to contextmenu
           */
          if (this.item.change !== constants.DETECTED_CHANGE_LEVEL.NONE) {
            this.connectToDataSource(this.item, true);
          }
        }

        if (result.success) {
          /** in case of add new connection string
           * then re-load to get connection string encryption
           */
          this.load();
          this.masterStore.loadList(uiState.tenantId);
          this.item.resetDirty();
          uiState.increaseDetectChangeCount();
          this.item.resetAvailableDataSourceChange();
          this.item.setAlertMessage({
            type: 'success',
            message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
          });
        } else {
          this.updateErrorMessage(result);
        }

        return result.messages;
      }.bind(this)
    );

    return dataFetcher.updateConnectionString(data).then(reloadStoreAfterSaved);
  });

  /**
   * update error message
   * @param  {Object} result
   */
  ConnectionDetailStore.prototype.updateErrorMessage = function(result) {
    var messageError =
      !isEmpty(result.messages) && result.messages[0].key === '' ? result.messages[0].messages[0] : null;
    this.item.setAlertMessage({ type: 'error', message: messageError });
  };

  module.exports = ConnectionDetailStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ConnectionDetailStore.js